import { useCallback, useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"
import { mergeClinicalHistoryWithQrData } from "../components/ClinicalHistoryHelpers"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

export default function useClinicalHistoryActions({
  patientId,
  clinicId,
  form,
  isFormDirty,
  isReadOnly,
  readOnlyReason,
  navigate,
  hub,
  setForm,
  onLoadHub,
  onOpenEndo,
  onOpenPerio,
  onOpenSurgery,
  onOpenOrtho,
  onOpenPediatric,
  onOpenDiagnosis,
}) {
  const [saving, setSaving] = useState(false)
  const [qrReviewModalOpen, setQrReviewModalOpen] = useState(false)
  const [markingQrReviewed, setMarkingQrReviewed] = useState(false)
  const [dismissedQrReviewId, setDismissedQrReviewId] = useState("")

  const persistClinicalHistory = useCallback(
    async ({ silentSuccess = false } = {}) => {
      try {
        if (isReadOnly) {
          alert(readOnlyReason)
          return false
        }

        if (!patientId) {
          alert("No hay patientId.")
          return false
        }

        if (!clinicId) {
          alert("No hay clinicId.")
          return false
        }

        setSaving(true)

        const res = await apiFetch(`${API_BASE}/clinical-history`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            patientId,
            clinicId,
            ...form,
          }),
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          alert(data?.message || "No se pudo guardar la historia clÃ­nica.")
          return false
        }

        if (!silentSuccess) {
          alert("Historia clÃ­nica guardada correctamente.")
        }

        await onLoadHub()
        return true
      } catch {
        alert("Error al guardar la historia clÃ­nica.")
        return false
      } finally {
        setSaving(false)
      }
    },
    [isReadOnly, readOnlyReason, patientId, clinicId, form, onLoadHub]
  )

  const handleSaveDraft = useCallback(() => {
    const key = `clinical_history_draft_${patientId || "no_patient"}_${clinicId || "no_clinic"}`
    localStorage.setItem(key, JSON.stringify(form))
    alert("Borrador guardado localmente.")
  }, [patientId, clinicId, form])

  const navigateToOdontogram = useCallback(() => {
    if (!patientId) {
      alert("No hay patientId.")
      return
    }

    if (!clinicId) {
      alert("No hay clinicId.")
      return
    }

    const params = new URLSearchParams()
    params.set("tab", "ODONTOGRAM")
    params.set("clinicId", clinicId)

    navigate(`/patients/${patientId}?${params.toString()}`)
  }, [patientId, clinicId, navigate])

  const handleOpenOdontogram = useCallback(
    async (saveFirst = false) => {
      if (saveFirst) {
        const ok = await persistClinicalHistory({ silentSuccess: true })
        if (!ok) return
        navigateToOdontogram()
        return
      }

      if (isFormDirty) {
        const saveBeforeLeaving = window.confirm(
          "Tienes cambios sin guardar. Aceptar = guardar y abrir odontograma. Cancelar = abrir sin guardar."
        )

        if (saveBeforeLeaving) {
          const ok = await persistClinicalHistory({ silentSuccess: true })
          if (!ok) return
        }
      }

      navigateToOdontogram()
    },
    [isFormDirty, persistClinicalHistory, navigateToOdontogram]
  )

  const handleOpenPatientsProfile = useCallback(() => {
    if (!patientId || !clinicId) return
    navigate(`/patients/${patientId}?clinicId=${clinicId}`)
  }, [patientId, clinicId, navigate])

  const handleBackToLauncher = useCallback(() => {
    const params = new URLSearchParams()
    if (clinicId) params.set("clinicId", clinicId)
    navigate(`/clinical-history${params.toString() ? `?${params.toString()}` : ""}`)
  }, [clinicId, navigate])

  const handleScrollToBaseForm = useCallback(() => {
    const node = document.getElementById("clinical-history-base-form")
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [])

  const handleOpenUpgrade = useCallback(() => {
    navigate("/upgrade-plan")
  }, [navigate])

  const handleUseQrIntakeData = useCallback(() => {
    const qrData = hub?.qrIntake?.dataJson || null

    if (!qrData) {
      alert("No hay captura QR disponible.")
      return false
    }

    setForm((prev) => mergeClinicalHistoryWithQrData(prev, qrData))
    return true
  }, [hub, setForm])

  const handleMarkQrReviewed = useCallback(
    async ({ silentSuccess = false } = {}) => {
      try {
        const formInstanceId = hub?.qrIntake?.latestInstanceId || ""

        if (!formInstanceId) {
          alert("No hay pre-registro QR pendiente.")
          return false
        }

        setMarkingQrReviewed(true)

        const res = await apiFetch(`${API_BASE}/clinical-history/form-instances/review`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            formInstanceId,
          }),
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          alert(data?.message || "No se pudo marcar como revisado.")
          return false
        }

        setDismissedQrReviewId(formInstanceId)
        setQrReviewModalOpen(false)
        await onLoadHub()

        if (!silentSuccess) {
          alert("Pre-registro QR marcado como revisado.")
        }

        return true
      } catch {
        alert("Error al marcar el pre-registro como revisado.")
        return false
      } finally {
        setMarkingQrReviewed(false)
      }
    },
    [hub, onLoadHub]
  )

  const handleUseQrIntakeDataAndMarkReviewed = useCallback(async () => {
    const merged = handleUseQrIntakeData()
    if (!merged) return false

    const reviewed = await handleMarkQrReviewed({ silentSuccess: true })
    if (!reviewed) return false

    alert("Se incorporaron los datos al formulario y la captura quedÃ³ revisada.")
    return true
  }, [handleUseQrIntakeData, handleMarkQrReviewed])

  const handleHubHistoryAction = useCallback(
    async (action) => {
      if (action === "SCROLL_BASE") {
        handleScrollToBaseForm()
        return
      }

      if (action === "OPEN_UPGRADE") {
        handleOpenUpgrade()
        return
      }

      if (action === "OPEN_ENDO") {
        await onOpenEndo()
        return
      }

      if (action === "OPEN_PERIO") {
        await onOpenPerio()
        return
      }

      if (action === "OPEN_SURGERY") {
        await onOpenSurgery()
        return
      }

      if (action === "OPEN_ORTHO") {
        await onOpenOrtho()
        return
      }

      if (action === "OPEN_PEDIATRIC") {
        await onOpenPediatric()
        return
      }

      if (action === "OPEN_DIAGNOSIS") {
        await onOpenDiagnosis()
        return
      }

      if (action === "OPEN_QR_REVIEW") {
        setQrReviewModalOpen(true)
        return
      }

      if (action === "OPEN_UNAVAILABLE") {
        alert("Esta historia especializada estarÃ¡ disponible prÃ³ximamente.")
      }
    },
    [
      handleScrollToBaseForm,
      handleOpenUpgrade,
      onOpenEndo,
      onOpenPerio,
      onOpenSurgery,
      onOpenOrtho,
      onOpenPediatric,
      onOpenDiagnosis,
    ]
  )

  const handleHubHistorySecondaryAction = useCallback(
    (item) => {
      if (item?.secondaryAction === "OPEN_ODONTOGRAM") {
        handleOpenOdontogram(false)
        return
      }

      if (item?.secondaryAction === "USE_QR_DATA") {
        handleUseQrIntakeData()
      }
    },
    [handleOpenOdontogram, handleUseQrIntakeData]
  )

  return {
    saving,
    qrReviewModalOpen,
    setQrReviewModalOpen,
    markingQrReviewed,
    dismissedQrReviewId,
    persistClinicalHistory,
    handleSaveDraft,
    handleOpenOdontogram,
    handleOpenPatientsProfile,
    handleBackToLauncher,
    handleOpenUpgrade,
    handleUseQrIntakeData,
    handleUseQrIntakeDataAndMarkReviewed,
    handleMarkQrReviewed,
    handleHubHistoryAction,
    handleHubHistorySecondaryAction,
  }
}
